// marked-config.js — global `marked` configuration for the headless render
// engine. Owned here (next to attachments.js, which consumes the data-attachment
// contract) rather than in the orchestrator: the attachment-image renderer is a
// single load-bearing point for HTML export's image inlining, and burying it in
// the host risked it being dropped when the on-screen preview surface was
// deleted. Both render entry points — the interactive orchestrator
// (markdown-renderer.js) and the headless export assembler (notes/export-import.js)
// — install it before rendering.
//
// Two `marked.use(...)` calls:
//   1. Disable indented code blocks. Indented text renders as normal
//      paragraphs; fenced code blocks (``` ... ```) still work.
//   2. Custom renderer: prevent CSP violations for attachment: images.
//      Emit <img data-attachment="file"> with no src — resolveAttachments()
//      (preview) / embedAttachmentsInHtml() (export) fills in the real data URI.
//      Omitting src avoids the legacy `src=""` reload quirk and keeps the
//      broken-image icon out of the surface while bytes load.

import { escapeHtml } from '../../utils/sanitize.js';

let _installed = false;

// Idempotent: marked.use() is additive, so guard against re-stacking the
// renderer when both the orchestrator and the export path call this.
/** Idempotently configures the vendor `marked` global. */
export function installMarkedConfig(): void {
  if (_installed) return;
  // `marked` is a vendor global; reach it off an any-cast window.
  const marked = ((typeof window !== 'undefined' ? window : {}) as any).marked;
  if (typeof marked === 'undefined') return;
  _installed = true;

  marked.use({
    breaks: true,
    tokenizer: {
      code(src: any): any {
        // Suppress the indented code block rule entirely
        const indentedCode = /^(?:(?: {4}|\t)[^\n]+(?:\n|$))+/;
        if (indentedCode.test(src)) return undefined;
      },
    },
  });

  const renderer = new marked.Renderer();
  const defaultImage = renderer.image.bind(renderer);
  renderer.image = ({ href, title, text }: any) => {
    if (href?.startsWith('attachment:')) {
      const filename = href.slice('attachment:'.length);
      const esc = (s: any) => escapeHtml(s, { forAttr: true });
      const titleAttr = title ? ` title="${esc(title)}"` : '';
      return `<img data-attachment="${esc(filename)}" alt="${esc(text || '')}"${titleAttr}>`;
    }
    return defaultImage({ href, title, text });
  };
  marked.use({ renderer });
}

// Install on module load so a bare `import './marked-config.js'` configures
// marked; the idempotent guard makes the explicit installMarkedConfig() calls
// in the render entry points safe.
installMarkedConfig();
